import {
  applyFormula,
  changeIndividualColumnState,
  setIsCustomColumnHeaderContextMenuOpen,
  setIsRenamePopoverOpen,
  setRenameColumnData,
  updateSortModel,
} from '@/redux/reducers/spreadsheetSlice';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem } from './MenuItem';
import './menulist.scss';

import ShowToast from '@/Components/Toast/showToastTemplate';
import { getClientState } from '@/Utils/ClientStateCommon';
import {
  MODAL_COMMENTS,
  MODAL_CREATE_ACCOUNT,
  MODAL_DELETE_COLUMN,
  MODAL_MAKE_COPY,
  PERMISSION_WRITE,
  TOAST_TYPE_ERROR,
} from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import {
  AG_GRID_AUTO_COLUMN,
  ROW_NUMBER_COLUMN_ID,
  ROW_NUMBER_COLUMN_NAME,
} from '@/Utils/gridConstants';
import { openFormulaPopover } from '@/helpers/spreadsheetHelper';
import useOnClickOutside from '@/hooks/useClickOutside';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import { datasetSliceName, userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import { QuickFilters } from './QuickFilters/QuickFilters';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';

export const CustomColumnHeaderContextMenu = ({
  agGrid,
  position,
  customContextMenuColumn: column,
}) => {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const currentFile = useCurrentFile();
  const [columnInfo, setColumnInfo] = useState(null);

  const menuRef = useRef();

  const {
    colId,
    colDef: { field, fieldType, headerName },
    aggregationActive,
  } = column || {};

  const isGridAutoColumn = colId === AG_GRID_AUTO_COLUMN;
  const isIdColumn = headerName === ROW_NUMBER_COLUMN_NAME;
  const groupedColumns = agGrid.api.getGridOption('columnDefs');
  const groupedColumnsList = agGrid.api.getRowGroupColumns();
  const isGrouped = groupedColumnsList.length > 0;
  const columnId = field || colId || groupedColumns[0]?.colId;

  const sharedFileWithPermission_READ =
    currentFile?.isFileShared && !currentFile.availablePermissions?.includes(PERMISSION_WRITE);
  const revenueBaseAccess =
    !!user?.userProperties?.revenueBaseAccess && sharedFileWithPermission_READ;
  const limitedAnalystMenu = isViewerAnalyst || revenueBaseAccess;

  useOnClickOutside(menuRef, () => dispatch(setIsCustomColumnHeaderContextMenuOpen(false)));

  const sortColumn = (sortType) => {
    dispatch(updateSortModel([{ colId: columnId, sort: sortType }]));
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const refreshCalculation = (event) => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    const fileAndFunction = '__FILE_AND_FUNCTION_NAME__';
    const toastErrorText =
      'An error occurred while refreshing the formula. Please try again later or contact support.';
    const showErrorToast = (errorContext) => {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: toastErrorText,
        errorContext: errorContext,
        fileAndFunction: fileAndFunction,
      });
    };

    try {
      event?.stopPropagation();
      const formulaToEdit = columnInfo[columnId].formula;
      formulaToEdit.columnId = columnId;
      dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
      dispatch(
        applyFormula({
          requestFormula: formulaToEdit.resolved,
          columnName: headerName,
          formulaToEdit,
          insertNewColumnNextTo: columnId,
          agGrid,
          currentModalOption: 'formula',
          fileAndFunction,
          toastErrorText,
        })
      )
        .then(() => {
          // do nothing...
        })
        .catch((error) => {
          showErrorToast(error);
        });
    } catch (error) {
      showErrorToast(error);
    }
  };

  const commentColumn = () => {
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));

    let modalName = null;

    if (user && user.is_anonymous) {
      modalName = MODAL_CREATE_ACCOUNT;
    } else if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    } else if (sharedFileWithPermission_READ) {
      modalName = MODAL_MAKE_COPY;
    }

    if (modalName) {
      dispatch(
        showModal({
          name: modalName,
          props: {
            columnId,
            columnName: headerName,
          },
        })
      );
      return;
    }

    dispatch(
      showModal({
        name: MODAL_COMMENTS,
        props: {
          column: column.colId,
          row: -1,
          isColumnComment: true,
          showAllComments: false,
        },
      })
    );
  };

  const groupColumn = () => {
    if (isPreviewMode && groupedColumnsList.length >= 1) {
      return showPreviewModeUpsellModal('Add Additional Group Conditions on the Premium Plan');
    }
    dispatch(changeIndividualColumnState({ colId, rowGroup: true, hide: true }));
    dispatch(
      addMixpanelEvent({
        eventName: 'Group By Used',
        eventProps: {
          Location: 'column header context menu',
          current_filename: currentFile.metadata?.FileName,
        },
        userIncrementName: '# of group by use',
      })
    );
    agGrid.api.ensureColumnVisible(ROW_NUMBER_COLUMN_ID);
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const deleteColumn = () => {
    let modalName = null;

    if (user && user.is_anonymous) {
      modalName = MODAL_CREATE_ACCOUNT;
    } else if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    } else if (sharedFileWithPermission_READ) {
      modalName = MODAL_MAKE_COPY;
    } else {
      modalName = MODAL_DELETE_COLUMN;
    }

    dispatch(
      showModal({
        name: modalName,
        props: {
          columnId,
          columnName: headerName,
        },
      })
    );
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const hideColumn = () => {
    const isAnonymous = user?.is_anonymous;
    const isReadPermission = sharedFileWithPermission_READ;
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));

    if (isAnonymous) {
      dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
      return;
    }

    if (isReadPermission && !isAnonymous) {
      return dispatch(showModal({ name: MODAL_MAKE_COPY }));
    }

    dispatch(changeIndividualColumnState({ colId, hide: true }));
  };

  const insertColumn = () => {
    let modalName = null;

    if (user && user.is_anonymous) {
      modalName = MODAL_CREATE_ACCOUNT;
    } else if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    } else if (sharedFileWithPermission_READ) {
      modalName = MODAL_MAKE_COPY;
    }

    if (modalName) {
      dispatch(
        showModal({
          name: modalName,
          props: {
            columnId,
            columnName: headerName,
          },
        })
      );
      return;
    }

    openFormulaPopover({ mode: 'formula', columnId });
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const renameColumn = () => {
    let modalName = null;

    if (user && user.is_anonymous) {
      modalName = MODAL_CREATE_ACCOUNT;
    } else if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    } else if (sharedFileWithPermission_READ) {
      modalName = MODAL_MAKE_COPY;
    } else {
      dispatch(setRenameColumnData({ colId: columnId, columnName: headerName }));
      modalName = dispatch(setIsRenamePopoverOpen(true));
    }

    dispatch(
      showModal({
        name: modalName,
        props: {
          columnId: columnId,
          columnName: headerName,
        },
      })
    );
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const editFormula = () => {
    let modalName = null;

    if (user && user.is_anonymous) {
      modalName = MODAL_CREATE_ACCOUNT;
    } else if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    } else if (sharedFileWithPermission_READ) {
      modalName = MODAL_MAKE_COPY;
    } else {
      modalName = null;
    }

    if (modalName) {
      return dispatch(
        showModal({
          name: modalName,
          props: {
            columnId: columnId,
            columnName: headerName,
          },
        })
      );
    }

    openFormulaPopover({
      mode: 'formula',
      columnId: columnId,
      formulaToEdit: {
        columnName: headerName,
        formula: columnInfo[columnId].formula,
      },
    });
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const getSortName = (type = 'asc') => {
    return (fieldType !== 'String' && fieldType !== 'EmailAddress') ||
      (isGrouped && aggregationActive)
      ? `Sort ${type === 'asc' ? 'Ascending - 1 to 9' : 'Descending - 9 to 1'}`
      : `Sort ${type === 'asc' ? 'Ascending - A to Z' : 'Descending - Z to A'}`;
  };

  const getColumnContextMenuItems = (menuItemsToShow, columnId, columnInfo) => {
    const shouldShowEditFormula =
      columnInfo && columnInfo?.[columnId]?.formula && Object.keys(columnInfo).includes(columnId);

    const items = [
      { name: getSortName('asc'), iconName: 'sort-ascending', action: () => sortColumn('asc') },
      { name: getSortName('dsc'), iconName: 'sort-descending', action: () => sortColumn('desc') },
      { name: 'separator', iconName: '', action: () => null },
      {
        name: 'Insert Column',
        iconName: 'plus-circle',
        action: insertColumn,
        hidden: limitedAnalystMenu,
      },
      { name: 'Group', iconName: 'group', action: groupColumn },
      {
        name: 'Rename',
        iconName: 'pencil-simple',
        action: renameColumn,
        hidden: limitedAnalystMenu,
      },
      {
        name: 'Edit Formula',
        iconName: 'function',
        action: () => editFormula(),
        submenu: [
          {
            name: 'Refresh Calculation',
            iconName: 'refresh',
            action: refreshCalculation,
          },
        ],
        hidden: !shouldShowEditFormula || limitedAnalystMenu, // Hide the menu item if the columnId is not a key in columnInfo
      },
      {
        name: 'Hide',
        iconName: 'eye-slash',
        action: hideColumn,
      },
      { name: 'Delete', iconName: 'trash', action: deleteColumn, hidden: limitedAnalystMenu },
      {
        name: 'Comment',
        iconName: 'chat-text',
        action: commentColumn,
        hidden: limitedAnalystMenu,
      },
    ];

    return items
      .filter((item) => menuItemsToShow.includes(item.name))
      .filter((item) => !item.hidden); // Exclude hidden items from the final list
  };

  useEffect(() => {
    const getColumnInfo = async () => {
      await getClientState(getFileUuidFromPath()).then((metadata) => {
        setColumnInfo(metadata.ColumnInfo);
      });
    };

    getColumnInfo();
  }, []);

  const columnMenuItems =
    isIdColumn || isGridAutoColumn
      ? getColumnContextMenuItems(
          ['Insert Column', 'Group', 'Hide', 'separator', getSortName('asc'), getSortName('dsc')],
          colId,
          columnInfo
        )
      : getColumnContextMenuItems(
          [
            'Insert Column',
            'Group',
            'Rename',
            'Edit Formula',
            'Hide',
            'Delete',
            'separator',
            getSortName('asc'),
            getSortName('dsc'),
            'Comment',
          ],
          colId,
          columnInfo
        );

  const style = {
    position: 'absolute',
    top: !isGrouped ? position.y + 5 : position.y + 50,
    left: position.x,
    zIndex: 10,
    overflow: 'visible',
    background: '#fcfcff',
  };

  return (
    <div ref={menuRef} className='p-1 rounded custom-context-menu' style={style}>
      <div className='tabs-cust menu-cust'>
        {columnMenuItems.map((item, index) => (
          <MenuItem key={index} item={item} />
        ))}
        <div>{!isGrouped && <QuickFilters />}</div>
      </div>
    </div>
  );
};
